import { ApolloError, useLazyQuery } from '@apollo/client'
import { FirebaseError, isSafari } from '@firebase/util'
import { Button, CircularProgress, Container, Link } from '@mui/material'
import ExternalLink from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { useFlag } from '@unleash/proxy-client-react'
import { getApp } from 'firebase/app'
import {
  UserCredential,
  fetchSignInMethodsForEmail,
  getAuth,
  getRedirectResult,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect
} from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { capitalize, difference, filter, has, intersection, merge } from 'lodash'
import { isAndroid, isIOS, isMobile } from 'mobile-device-detect'
import rg4js from 'raygun4js'
import { ChangeEvent, FormEvent, ReactElement, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import ViewTitle from '../../components/ViewTitle'
import PasswordVisibilityAdornment from '../../components/forms/PasswordVisibilityAdornment'
import StyledInputLabel from '../../components/forms/StyledInputLabel'
import StyledTextField from '../../components/forms/StyledTextField'
import { IdpFeatureFlagMap, LoginProvider, getLoginProviderConfig, supportedLoginProviderIds } from '../../config/firebase'
import AppleAuthProvider from '../../config/idps/apple'
import RequiredParameterException from '../../errors/RequiredParameter'
import { BucketsByUserIdDocument, UserStateStatusDocument } from '../../graphql/generated'
import { modalVar } from '../../providers/apollo/cache'
import logger from '../../utils/logger'
import AuthFormFirebase from './components/AuthFormFirebase'
import EnterEmailDialog from './components/EnterEmailDialog'
import HelpDialog from './components/HelpDialog'
import InvitationHeader from './components/InvitationHeader'
import SSOButton, { IdpContainer } from './components/SSOButton'
import { StyledFormError, StyledFormInfo } from './components/StyledFormAlert'
import StyledFormButton from './components/StyledFormButton'
import StyledFormHeader from './components/StyledFormHeader'
import StyledFormHelperTypography from './components/StyledFormHelperTypography'
import StyledFormLegalDisclaimerTypography from './components/StyledFormLegalDisclaimerTypography'
import StyledFormLinkBold from './components/StyledFormLinkBold'
import StyledFormPBLogo from './components/StyledFormPBLogo'
import StyledFormSubheader from './components/StyledFormSubheader'
import { ScreenPhase } from './components/types'
import useAppleLogin from './hooks/useAppleLogin'
import useFirebaseTracing from './hooks/useFirebaseTracing'
import useGAEvent from './hooks/useGAEvent'
import useLoginFirebase from './hooks/useLogin'

export const REDIRECT_LOCAL_STORAGE_VAR = 'redirect_idp'

export type Field = 'username' | 'password'

export interface Fields {
  username?: string
  password?: string
  // 👇 for EnterEmailDialog
  enterEmail?: string
}

interface FieldErrors extends Fields {
  general?: string
}

interface FirebaseAddEmailRequest {
  email: string
  idToken: string
}

interface FirebaseAddEmailResponse {
  error?: FirebaseError
  firebaseToken: string
}

interface FirebaseAuthRequest {
  identifier: string
  password: string
}
interface FirebaseAuthResponse {
  error?: FirebaseError
  firebaseToken: string
}

const VALID_EMAIL_ACTIONS = ['link', 'login', 'resetPassword', 'recoverAccount'] as const
type EmailAction = typeof VALID_EMAIL_ACTIONS[number]

interface FirebaseEmailRequest {
  action: EmailAction
  identifier: string
  url: string
}

interface FirebaseEmailResponse {
  message?: string
  error?: FirebaseError
}

const emailRegex = /\S+@\S+\.\S+/
const isValidEmail = (email: string) => emailRegex.test(email)

/**
 * Renders Login Form with Captcha
 */
export default function Login() {
  // browser location
  const location = useLocation()
  const searchQueryRef = useRef(location.search)
  const searchParams = new URLSearchParams(location.search)
  const encryptedUserId = searchParams.get('state') ?? null

  // username & email / password state
  const [fields, setFields] = useState<Fields>({})
  const [errors, setErrors] = useState<FieldErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const { attemptCustomTokenLogin, attemptSubscriptionTokenLogin, attemptRedirect, results, subscriptionTokenLoginState } = useLoginFirebase()
  const fetchBucketsRef = useRef(attemptRedirect)
  const [userStatusQuery] = useLazyQuery(UserStateStatusDocument)
  const [getBucketsQuery] = useLazyQuery(BucketsByUserIdDocument)
  const ssoProviderId = useRef<string>(localStorage.getItem(REDIRECT_LOCAL_STORAGE_VAR) || '')

  // helper dialog
  const hasSeenHelpDialog = useRef(false)
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState<boolean>(false)
  const [userRecentLoginProviderIds, setUserRecentLoginProviderIds] = useState<string[]>([])

  // enter email dialog
  const [isEnterEmailLoading, setIsEnterEmailLoading] = useState(false)
  const [isEnterEmailDialogOpen, setIsEnterEmailDialogOpen] = useState(false)
  const providerIdForEmailPopup = useRef('')
  const idTokenToUseAfterEmailPopup = useRef('')

  // blocks login button
  const [isAttemptingLogin, setIsAttemptingLogin] = useState(false)

  // redirect login screen state - shows spinner if true
  const [isAttemptingRedirectLogin, setIsAttemptingRedirectLogin] = useState(Boolean(localStorage.getItem(REDIRECT_LOCAL_STORAGE_VAR)))
  const isAttemptingCustomTokenLogin = subscriptionTokenLoginState === 'loading' || subscriptionTokenLoginState === 'success'

  // magic link state
  const [isAttemptingToSend, setIsAttemptingToSend] = useState(false)
  const [screenPhase, setScreenPhase] = useState(ScreenPhase.FORM)

  // Initialize Firebase
  const functions = getFunctions(getApp())
  const { init } = useAppleLogin()
  const initAppleRef = useRef(init)
  const { mark, startTracing, stopTracing } = useFirebaseTracing('LOGIN_TRACE')
  const { trackEvent } = useGAEvent()

  // individual idp flags
  const idpFeatureFlags: IdpFeatureFlagMap = {
    isFirebaseAppleIdpEnabled: useFlag('isFirebaseAppleIdpEnabled'),
    isFirebaseGoogleIdpEnabled: useFlag('isFirebaseGoogleIdpEnabled'),
    isFirebaseFacebookIdpEnabled: useFlag('isFirebaseFacebookIdpEnabled'),
    isFirebaseMicrosoftIdpEnabled: useFlag('isFirebaseMicrosoftIdpEnabled'),
    isFirebasePaypalIdpEnabled: useFlag('isFirebasePaypalIdpEnabled')
  }

  const hasLogoutParam = new URLSearchParams(location.search).get('logout') === 'true'

  const enabledIdpCount = filter(idpFeatureFlags, (flag) => flag === true)

  // feature flag for sso provider
  // will evaluate to false if no idps are enabled
  const isFirebaseSsoEnabled = useFlag('isFirebaseSsoEnabled') && enabledIdpCount.length > 0

  // helper functions
  const closeEnterEmailDialog = () => setIsEnterEmailDialogOpen(false)
  const isLoginButtonDisabled = () => isAttemptingLogin || results.loading
  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const closeHelpDialog = () => setIsHelpDialogOpen(false)

  const sendMagicLink = async () => {
    // prevents spam clicking
    if (isAttemptingToSend) {
      return
    }

    try {
      const { username } = fields
      if (!username) {
        throw new RequiredParameterException('Email is required', 'username')
      }

      setIsAttemptingToSend(true)

      const addMessage = httpsCallable<FirebaseEmailRequest, FirebaseEmailResponse>(functions, 'firebaseEmail')
      const { data } = await addMessage({ identifier: username, action: 'login', url: window.location.href })

      setIsAttemptingToSend(false)
      setErrors({})
      const { error } = data
      if (error) {
        throw error
      }

      if (isValidEmail(username)) {
        localStorage.setItem('_pb_linkSignInEmail', username)
      } else {
        localStorage.removeItem('_pb_linkSignInUsername')
      }

      setScreenPhase(ScreenPhase.EMAIL_SENT)
    } catch (err) {
      const error = err as RequiredParameterException
      const emailName = 'magic link'

      switch (error.code) {
        case 'RequiredParameterException':
          setErrors({ [error.parameterName]: error.message })
          break
        case 'auth/too-many-requests':
        case 'auth/invalid-email':
        case 'auth/invalid-identifier':
        case 'auth/user-not-found':
        case 'auth/import-failed':
          setScreenPhase(ScreenPhase.EMAIL_SENT)
          break
        default:
          setIsAttemptingToSend(false)
          setErrors({ general: `There was an error sending the ${emailName} email. Please resubmit your username or email address.` })
      }
    }
  }

  const openHelpDialog = () => {
    setIsHelpDialogOpen(true)
    hasSeenHelpDialog.current = true
  }

  /**
   * Collects sign in methods for a user based on their email. If the user has not seen a HelpDialog box yet,
   * one will be shown.
   *
   * @param userIdentityProviderIds - the ids of sso login options this user has linked to the email or username they attempted to login with
   * @param errorMessage - the message to be displayed when the user fails to login
   * @param errorField - controls where the error is display, see {@link FieldErrors}
   *
   * @returns boolean - true if the box opened, false if not
   */
  const showHelpDialogOrErrorAlert = async (
    userIdentityProviderIds: string[],
    errorMessage: ReactElement<any, any> | string,
    errorField: keyof FieldErrors = 'general'
  ): Promise<boolean> => {
    // all the SSO providers the user has access to that are valid
    const usableLoginProviderIds = intersection(userIdentityProviderIds, supportedLoginProviderIds)

    // filters disabled idps that are disabled in unleash out of the list of usable login providers
    const enabledLoginProviders = usableLoginProviderIds.filter((providerId) => {
      const { featureFlagName } = getLoginProviderConfig(providerId)
      const enabled = idpFeatureFlags[featureFlagName]

      return enabled
    })

    // does a quick comparison of the current state (userRecentLoginProviderIds)
    // against what we are about to attempt to update it to (usableLoginProviderIds)
    // to avoid a re-render if no change is needed
    const needsToUpdateProviders = difference(enabledLoginProviders, userRecentLoginProviderIds).length > 0

    // additionally, check if any providers were found for the user
    if (isFirebaseSsoEnabled && enabledLoginProviders.length > 0) {
      if (needsToUpdateProviders) {
        setUserRecentLoginProviderIds(enabledLoginProviders)
      }

      if (!hasSeenHelpDialog.current) {
        openHelpDialog()
        return true
      }
    }

    setErrors({ [errorField]: errorMessage })
    return false
  }

  const handleProviderError = async (error: FirebaseError, attemptHelpDialogOnFail: boolean = false) => {
    // Handle errors and provide context here
    const { code } = error
    let { message } = error

    const provider = getLoginProviderConfig(ssoProviderId.current).getProvider()
    const providerName = getLoginProviderConfig(provider.providerId).name

    // banned user
    if (message.toLowerCase().includes('user is not active, please contact support')) {
      setErrors({ general: 'User is not active, please contact support.' })
      return
    }

    if (message.includes('account-exists-with-different-credential')) {
      const email: string = error.customData?.email as string

      if (email) {
        const formattedEmail = email ? ` (${email})` : ''
        message = `An account exists with this email${formattedEmail}. You can enable single click sign on with ${providerName} under your account settings.`

        if (attemptHelpDialogOnFail) {
          let userIdentityProviderIds: string[] = []
          try {
            userIdentityProviderIds = await fetchSignInMethodsForEmail(getAuth(), String(email))
          } catch (fetchErr) {
            logger.error(fetchErr)
          }

          await showHelpDialogOrErrorAlert(userIdentityProviderIds, message)
        }
      }
    } else {
      if (message === 'The user with the provided uid already exists.') {
        message = `An account exists with the same email, but a different identity provider. If you are having trouble accessing your account, try resetting your password. You can link your\
          ${capitalize(providerName)} account in your settings.`
      }

      if (code === 'auth/user-cancelled') {
        message = 'Please try again and grant the requested permissions to continue.'
      }

      // we don't need to display an error when the user closes the popup window
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return
      }

      setErrors({ general: message })
    }

    logger.error({
      code,
      error,
      userFacingErrorMessage: message
    })
  }

  const handleProviderErrorRef = useRef(handleProviderError)

  const attemptEmailChange = async () => {
    try {
      if (!fields.enterEmail) {
        throw new FirebaseError('auth/missing-email', 'You must enter your email to continue')
      }

      setIsEnterEmailLoading(true)
      const addMessage = httpsCallable<FirebaseAddEmailRequest, FirebaseAddEmailResponse>(functions, 'firebaseAddEmail')
      const { data } = await addMessage({ email: fields.enterEmail, idToken: idTokenToUseAfterEmailPopup.current })

      const { error, firebaseToken } = data
      if (error) {
        throw error
      }

      signInWithCustomToken(getAuth(), firebaseToken)
      trackEvent('custom_token_created_event', {
        metric: 'custom_token_created_event',
        email: fields.enterEmail,
        platform: 'WEB'
      })
    } catch (err) {
      setIsEnterEmailLoading(false)
      const error = err as FirebaseError
      let message: string
      switch (error.code) {
        case 'auth/email-taken':
          message = 'An account with this email address already exists.'
          break
        case 'auth/email-format':
          message = 'Invalid email format.'
          break
        default:
          message = 'There was an error setting your email address. If this issue persists, please reload the page and try again.'
      }
      setErrors({ enterEmail: message })
      trackEvent('custom_token_created_error', {
        metric: 'custom_token_created_error',
        platform: 'WEB',
        clientErrors: error.cause,
        name: error.name,
        message
      })
    }
  }

  /**
   * Handles single click sign on for a {@link LoginProvider} (like Google, facebook, etc)
   *
   * @param provider a provide which extends Firebase's {@link BaseOAuthProvider}
   * @param attemptHelpDialogOnFail whether or not to open a helper dialog
   */
  const handleProvider = async (provider: LoginProvider) => {
    startTracing()
    mark('handleProvider')
    rg4js('send', {
      event: `handleProvider#${provider.providerId}`,
      path: window.location.pathname
    })
    setIsAttemptingLogin(true)
    ssoProviderId.current = provider.providerId

    mark(`handleProvider#${provider.providerId}`)
    // custom flow for apple SAML login
    if (provider.providerId === AppleAuthProvider.PROVIDER_ID) {
      mark('handleProvider#apple')
      try {
        const response = await window.AppleID?.auth?.signIn().catch((err: Error) => {
          logger.error(err)
          setIsAttemptingLogin(false)
          setErrors({ general: 'There was an error retrieving your Apple authorization' })
          trackEvent('login_failure', { provider: provider.providerId, error: JSON.stringify(err) })
        })

        if (!response?.authorization) {
          logger.error('Failed to retrieve apple auth')
          return
        }

        // signs in popup users
        const appleCredential = new AppleAuthProvider().credential({ idToken: response.authorization.id_token })
        signInWithCredential(getAuth(), appleCredential).catch((err) => {
          logger.error(err)
          setIsAttemptingLogin(false)
          setErrors({ general: 'There was an error retrieving your credentials.' })
          trackEvent('login_failure', { provider: provider.providerId, error: JSON.stringify(err) })
        })
        rg4js('send', {
          event: 'appleCredentialRetrieved',
          path: window.location.pathname
        })
      } catch (err) {
        logger.error(err)
      }

      return
    }

    if (isMobile) {
      window.localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, provider.providerId)
      signInWithRedirect(getAuth(), provider).catch((err) => {
        logger.error(err)
        setIsAttemptingLogin(false)
        setErrors({ general: `There was an error signing into your ${provider.providerId} account.` })
        trackEvent('login_failure', { provider: provider.providerId, error: JSON.stringify(err) })
      })
      rg4js('send', {
        event: 'signInWithRedirect',
        path: window.location.pathname
      })
      return
    }

    ssoProviderId.current = provider.providerId
    signInWithPopup(getAuth(), provider).catch((err) => {
      if (err.code === 'auth/popup-blocked') {
        window.localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, provider.providerId)
        signInWithRedirect(getAuth(), provider)
        return
      }
      setIsAttemptingLogin(false)
      logger.error(err)
      setErrors({ general: `There was an error signing into your ${provider.providerId} account.` })
      trackEvent('login_failure', { provider: provider.providerId, error: JSON.stringify(err) })
    })
    rg4js('send', {
      event: 'signInWithRedirect',
      path: window.location.pathname
    })
  }

  const handleFirebaseLogin = async (event?: FormEvent) => {
    event?.preventDefault()
    startTracing()
    mark('handleFirebaseLogin')
    const { username, password } = fields

    try {
      if (!username) {
        throw new RequiredParameterException('Username is required', 'username')
      }

      if (!password) {
        throw new RequiredParameterException('Password is required', 'password')
      }

      mark('handleFirebaseLogin#attempting')
      setIsAttemptingLogin(true)

      mark('handleFirebaseLogin#addMessage')
      rg4js('send', {
        event: 'signInFromForm',
        identifier: username,
        path: window.location.pathname
      })
      const addMessage = httpsCallable<FirebaseAuthRequest, FirebaseAuthResponse>(functions, 'firebaseAuth')
      const { data } = await addMessage({ identifier: username, password })

      const { error, firebaseToken } = data
      if (error) {
        throw error
      }

      trackEvent('login_success', { username })
      trackEvent('login')
      mark('handleFirebaseLogin#signInWithCustomToken')
      signInWithCustomToken(getAuth(), firebaseToken)
      rg4js('send', {
        event: 'signInFromForm#customToken',
        path: window.location.pathname
      })
    } catch (err) {
      trackEvent('login_failure', { username, error: JSON.stringify(err) })
      rg4js('send', {
        event: 'signInFromForm#error',
        error: err,
        path: window.location.pathname
      })
      mark('handleFirebaseLogin#catch', err)
      stopTracing()

      setIsAttemptingLogin(false)

      const error = err as RequiredParameterException
      const firebaseError = err as FirebaseError

      let message: ReactElement<any, any> | string = firebaseError.message ?? 'Login attempt failed'
      const userIdentityProviderIds: string[] = (firebaseError.customData?.userProviderIds as string[]) || []

      switch (error.code) {
        case 'auth/too-many-requests':
          setErrors({ general: 'Too many failed login attempts. Use "Forgot Password?" or try again later.' })
          break

        case 'auth/login':
          message = (
            <>
              <StyledFormHelperTypography sx={{ fontSize: '18px' }}>
                Please check the accuracy of your email/username and password. If you haven&apos;t accessed your account lately, use the reset
                password link to reset your password as we may have locked your account for security purposes.
              </StyledFormHelperTypography>
              <StyledFormLinkBold
                style={{ fontWeight: 400, fontSize: '18px', marginTop: '10px', display: 'block' }}
                data-testid="forgot-password-button"
                to={`/auth/forgot-password${location.search}`}
              >
                Reset Password
              </StyledFormLinkBold>

              <>
                {/* Magic Link - active */}
                {!isAttemptingToSend && (
                  <Button
                    style={{ fontWeight: 400, fontSize: '18px', marginTop: '10px', display: 'block' }}
                    id="loginLink"
                    variant="text"
                    data-testid="send-link"
                    onClick={sendMagicLink}
                  >
                    Send me a one time login link <br />
                    (no password required)
                  </Button>
                )}

                {/* Magic Link - disabled */}
                {isAttemptingToSend && (
                  <Button
                    style={{ fontWeight: 400, fontSize: '18px', marginTop: '10px', display: 'block' }}
                    id="loginLink"
                    data-testid="send-link"
                    variant="text"
                  >
                    Send me a one time login link <br />
                    (no password required)
                  </Button>
                )}

                {/* Acts as a spacer between the links. style and sx props don't work on the Link tag */}
                <Container maxWidth={false} sx={{ my: 2 }} />
              </>

              {/* Magic Link - disabled */}
              {isAttemptingToSend && (
                <Button
                  style={{ fontWeight: 400, fontSize: '18px', marginTop: '10px', display: 'block' }}
                  id="loginLink"
                  data-test="send-link"
                  variant="text"
                >
                  Send me a one time login link <br />
                  (no password required)
                </Button>
              )}

              {/* Acts as a spacer between the links. style and sx props don't work on the Link tag */}
              <Container maxWidth={false} sx={{ my: 2 }} />
            </>
          )
          showHelpDialogOrErrorAlert(userIdentityProviderIds, message, 'general')
          break

        case 'RequiredParameterException':
          setErrors({ [error.parameterName]: error.message })
          break

        default:
          setErrors({ general: message || 'There was an error signing in' })
      }

      logger.error(err)
    }
  }

  const checkTermsStatus = async () => {
    const { uid: userId } = getAuth().currentUser ?? {}
    if (!userId) {
      throw new FirebaseError('auth/unauthenticated', 'Failed to fetch buckets. Unauthenticated.')
    }

    // Don't show terms and conditions to PUC users
    const idToken = await getAuth().currentUser?.getIdTokenResult()
    const isPUCUser = idToken?.claims?.isPUCUser ?? false

    const variables = { userId }
    const res = await getBucketsQuery({ variables })
    if (!isPUCUser && res.data?.userProfile?.latestTermsAccepted !== true) {
      logger.info('Terms and conditions not accepted')
      modalVar('termsAndConditionsLogin')
    } else {
      await fetchBucketsRef.current()
    }
  }

  // Uses firebase auth listener to determine user has successfully signed in on firebase sdk,
  // then signs user into the graph api.
  useEffectOnce(() => {
    const unsubscribe = getAuth().onAuthStateChanged(async (user) => {
      // prevents multipe requests from being made during rerenders
      if (!user || results.loading || results.data) {
        return
      }

      const provider = getLoginProviderConfig(user.providerId)

      // logs the user into our graph api once firebase recognizes a user
      try {
        mark('useEffect#userStatusCheck')

        if (provider && user.email) {
          const userStatus = await userStatusQuery()
          const data = userStatus.data?.userStateStatus

          // Redirect User to accidental Idp Click Page
          if (!data?.kafka && data?.firebase) {
            window.location.href = '/auth/no-account-idp/'
            return
          }
        }

        mark('useEffect#attemptRedirect')
        rg4js('send', {
          event: `onAuthStateChanged#attemptRedirect`,
          path: window.location.pathname
        })

        await checkTermsStatus()

        mark('useEffect#success')
      } catch (err) {
        rg4js('send', {
          event: `onAuthStateChanged#error`,
          error: err,
          path: window.location.pathname
        })
        mark('useEffect#catch', err)
        stopTracing()

        logger.error(err)
        localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, '')
        setIsAttemptingRedirectLogin(false)

        // handle  apollo graphql error
        if (has(err, 'graphQLErrors')) {
          const apolloErrors = err as ApolloError
          logger.error(apolloErrors)

          // apollo errors do not have a code property, so we add one using lodash#merge
          const error = merge(apolloErrors.clientErrors[0] || apolloErrors.graphQLErrors[0], { code: 'apollo-error' })
          handleProviderErrorRef.current(error, true)
          return
        }

        // handle firebase error
        const error = err as FirebaseError
        handleProviderErrorRef.current(error, true)
        results.reset()
      }
    })

    return () => unsubscribe()
  })

  // capture event for page view
  useEffectOnce(() => {
    trackEvent('login_view')
  })

  // attempts to sign a user in using a custom token or subscription token
  useEffectOnce(() => {
    if (encryptedUserId) {
      attemptSubscriptionTokenLogin()
    } else {
      attemptCustomTokenLogin()
    }
  })

  // attempt apple redirect sign in with credential
  useEffectOnce(() => {
    const params = new URLSearchParams(searchQueryRef.current)
    const attemptAppleRedirectSignIn = params.get('provider') === 'apple'
    const idToken = params.get('id_token')

    if (!idToken) {
      return
    }

    if (attemptAppleRedirectSignIn) {
      startTracing()
      mark('useEffect#appleAuthCredential')
      const credential = new AppleAuthProvider().credential({ idToken })
      mark('useEffect#appleSignInWithCredential')
      signInWithCredential(getAuth(), credential)
        .then(() => {
          mark('useEffect#appleResults')
        })
        .catch((error) => {
          mark('useEffect#appleError', error)
          logger.error(error)
        })
    }
  })

  // prevent webview
  useEffectOnce(() => {
    if (!isMobile) {
      return
    }
    const userAgent = window.navigator.userAgent.toLowerCase()

    if (isIOS && !userAgent.includes('safari')) {
      setErrors({
        general:
          'You are not accessing Photobucket through a secure browser. Please copy the link you were sent and paste it into your Safari or Chrome browser.'
      })
    }

    if (isAndroid && userAgent.includes('wv')) {
      setErrors({
        general:
          'You are not accessing Photobucket through a secure browser. Please copy the link you were sent and paste it into your Safari or Chrome browser.'
      })
    }
  })

  // init appleID auth
  useEffect(() => {
    initAppleRef.current('login')
  }, [])

  // redirect login for mobile & safari
  useEffect(() => {
    const redirectProviderId = localStorage.getItem(REDIRECT_LOCAL_STORAGE_VAR)

    if (!redirectProviderId) {
      return
    }

    getRedirectResult(getAuth())
      .then(async (result: UserCredential | null) => {
        logger.info({ result })
        if (!result) {
          let message = 'No results from redirect login.'
          if (isSafari()) {
            const providerName = capitalize(getLoginProviderConfig(redirectProviderId).name)
            message = `Safari Detected: There are some known issues with Safari blocking social logins. You can try signing in using ${providerName} again, or use your username and password.`
          }
          throw new Error(message)
        }
      })
      .catch((err: Error) => {
        logger.error(err)
        localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, '')
        setIsAttemptingRedirectLogin(false)

        // handle  apollo graphql error
        if (has(err, 'graphQLErrors')) {
          const apolloErrors = err as ApolloError
          logger.error(apolloErrors)

          // apollo errors do not have a code property, so we add one using lodash#merge
          const error = merge(apolloErrors.clientErrors[0] || apolloErrors.graphQLErrors[0], { code: 'apollo-error' })
          handleProviderErrorRef.current(error, true)
          return
        }

        // handle firebase error
        const error = err as FirebaseError
        handleProviderErrorRef.current(error, true)
      })
      .finally(() => localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, ''))
  }, [])

  // Handle change to textfields
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedFields = { ...fields }
    const field = event.target.name as Field
    updatedFields[field] = event.target.value
    setFields(updatedFields)

    const updatedErrors = { ...errors }
    delete updatedErrors[field]
    delete updatedErrors.general
    setErrors(updatedErrors)
  }

  if (isAttemptingRedirectLogin || isAttemptingCustomTokenLogin) {
    return (
      <AuthFormFirebase>
        <StyledFormPBLogo />
        <Typography variant="h4" sx={{ mt: 3 }}>
          Attempting to Login
        </Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </AuthFormFirebase>
    )
  }

  /**
   * @todo Remove BillHeader after new checkout is complete.
   */
  return (
    <AuthFormFirebase onSubmit={handleFirebaseLogin}>
      <InvitationHeader />
      {hasLogoutParam && (
        <StyledFormHelperTypography sx={{ mb: 2, textAlign: 'center' }}>You have successfully logged out.</StyledFormHelperTypography>
      )}
      <HelpDialog
        isOpen={isHelpDialogOpen}
        handleProvider={handleProvider}
        handleClose={closeHelpDialog}
        userLoginProviderIds={userRecentLoginProviderIds}
      />
      <EnterEmailDialog
        error={errors.enterEmail || ''}
        loading={isEnterEmailLoading}
        open={isEnterEmailDialogOpen}
        email={fields.enterEmail || ''}
        attemptEmailChange={attemptEmailChange}
        handleClose={closeEnterEmailDialog}
        handleChange={handleChange}
        providerName={getLoginProviderConfig(providerIdForEmailPopup.current)?.name || ''}
      />
      <ViewTitle title="Login" />
      <StyledFormHeader style={{ marginBottom: '12px' }}>Sign In</StyledFormHeader>
      {isFirebaseSsoEnabled && (
        <>
          {/*
           * Renders the login with google, facebook, and other SSO buttons.
           * To add a new button, simply add the config in firebase.ts.
           */}
          <IdpContainer>
            {supportedLoginProviderIds.map((providerId: string) => {
              const { featureFlagName, getProvider } = getLoginProviderConfig(providerId)
              const provider = getProvider()
              const key = `sso-login-${provider.providerId}`

              // hides this idp if not enabled in unleash
              if (idpFeatureFlags[featureFlagName] === false) {
                return null
              }

              return (
                <SSOButton
                  action="Sign In with"
                  disabled={isAttemptingLogin}
                  key={key}
                  providerId={providerId}
                  handleProvider={() => handleProvider(provider)}
                />
              )
            })}
          </IdpContainer>
        </>
      )}

      <StyledFormError title="Error logging in" body={errors.general} />

      {screenPhase === ScreenPhase.EMAIL_SENT && (
        <StyledFormInfo
          title="Email Sent"
          body={
            <>
              <StyledFormSubheader sx={{ textOverflow: 'ellipsis', whiteSpace: 'break-spaces', overflow: 'hidden' }}>
                {
                  fields.username?.includes('@')
                    ? `We've sent an email to ${fields.username}.` // input was email address
                    : `We've sent an email to the address associated with ${fields.username}.` // input was username
                }
                <br />
              </StyledFormSubheader>
              <StyledFormHelperTypography>
                Having trouble?
                <Link
                  underline="none"
                  sx={{ my: 2, fontSize: '14px' }}
                  target="_blank"
                  href="https://support.photobucket.com/hc/en-us/categories/4404271347732-Account-Recovery"
                >
                  {' '}
                  Visit help center
                </Link>
              </StyledFormHelperTypography>
            </>
          }
        />
      )}

      {screenPhase === ScreenPhase.FORM && (
        <>
          <StyledInputLabel sx={{ mt: '18px' }} htmlFor="username">
            Email/Username
          </StyledInputLabel>
          <StyledTextField
            autoFocus
            id="username"
            data-testid="username"
            disabled={isAttemptingLogin}
            error={Boolean(errors.username)}
            fullWidth
            helperText={errors.username}
            name="username"
            margin="dense"
            onChange={handleChange}
            placeholder="Enter a username"
            required
            type="text"
            InputLabelProps={{ shrink: true }}
            inputProps={{ autoCapitalize: 'off' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <StyledInputLabel htmlFor="password">Password</StyledInputLabel>
            <StyledFormLinkBold style={{ fontSize: '16px' }} data-testid="forgot-password-button" to={`/auth/forgot-password${location.search}`}>
              Forgot Password?
            </StyledFormLinkBold>
          </div>
          <StyledTextField
            id="password"
            data-testid="password"
            disabled={isAttemptingLogin}
            error={Boolean(errors.password)}
            fullWidth
            helperText={errors.password}
            name="password"
            data-sr-redact
            margin="dense"
            onChange={handleChange}
            required
            type={showPassword ? 'text' : 'password'}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              autoCapitalize: 'off',
              endAdornment: <PasswordVisibilityAdornment showPassword={showPassword} handleClickShowPassword={handleClickShowPassword} />
            }}
            placeholder=""
          />
          <StyledFormButton type="submit" data-testid="submit" color="primary" fullWidth variant="contained" disabled={isLoginButtonDisabled()}>
            Sign In
          </StyledFormButton>

          {/* Magic Link - active */}
          {!isAttemptingToSend && (
            <Button id="loginLink" style={{ fontSize: '16px' }} data-testid="magic-link" onClick={sendMagicLink} variant="text">
              Send me a one time login link (no password required)
            </Button>
          )}

          {/* Magic Link - disabled */}
          {isAttemptingToSend && (
            <Button id="loginLink" style={{ fontSize: '16px' }} data-testid="send-link" variant="text">
              Send me a one time login link (no password required)
            </Button>
          )}
        </>
      )}

      <StyledFormLegalDisclaimerTypography sx={{ fontSize: '16px', paddingTop: 2 }}>
        This site is protected by reCAPTCHA and the Google{' '}
        <ExternalLink style={{ color: '#1C47CB' }} href="https://policies.google.com/privacy" underline="always" data-testid="google-privacy">
          Privacy Policy
        </ExternalLink>{' '}
        and{' '}
        <ExternalLink style={{ color: '#1C47CB' }} href="https://policies.google.com/terms" underline="always" data-testid="google-terms-of-service">
          Terms of Service
        </ExternalLink>{' '}
        apply.
      </StyledFormLegalDisclaimerTypography>

      <StyledFormHelperTypography sx={{ mt: '25px', fontSize: '18px' }}>
        Don&apos;t have an account?{' '}
        <StyledFormLinkBold data-testid="create-account-button" to={`/auth/register${location.search}`}>
          Sign Up
        </StyledFormLinkBold>
      </StyledFormHelperTypography>
    </AuthFormFirebase>
  )
}
